/**
 * B4aLoader Standalone Component
 * 
 * A completely self-contained loading animation component.
 * No external CSS or dependencies required - just copy and paste this file.
 * 
 * Design System Colors:
 * - Green (cta-green): #27AE60
 * - White: #F9F9F9
 * - Font: Inter
 * 
 * Style: Solid green dots with glow effect
 * 
 * Usage:
 *   import B4aLoader from './B4aLoaderStandalone';
 *   <B4aLoader />
 *   <B4aLoader text="Creating app..." />
 *   <B4aLoader text={null} size="lg" />
 */

interface B4aLoaderProps {
  /** Optional custom text. Pass null to hide text completely. Defaults to "Loading..." */
  text?: string | null;
  /** Optional size variant: 'sm', 'md', or 'lg' */
  size?: 'sm' | 'md' | 'lg';
}

const B4aLoader = ({ text = 'Loading...', size = 'md' }: B4aLoaderProps) => {
  // Size configurations
  const sizeConfig = {
    sm: { dot: 10, gap: 8, bounce: 12, fontSize: 12 },
    md: { dot: 14, gap: 10, bounce: 16, fontSize: 14 },
    lg: { dot: 16, gap: 12, bounce: 18, fontSize: 16 },
  };

  const config = sizeConfig[size];

  // Design system color
  const ctaGreen = '#27AE60';

  // Inline styles for the animation (no external CSS needed)
  const keyframesStyle = `
    @keyframes b4aDotGlow {
      0%, 80%, 100% {
        transform: translateY(0) scale(1);
        box-shadow: 
          0 0 12px rgba(39, 174, 96, 0.6),
          0 0 24px rgba(39, 174, 96, 0.4),
          0 0 36px rgba(39, 174, 96, 0.2);
      }
      40% {
        transform: translateY(-${config.bounce}px) scale(1.15);
        box-shadow: 
          0 0 16px rgba(39, 174, 96, 0.8),
          0 0 32px rgba(39, 174, 96, 0.5),
          0 0 48px rgba(39, 174, 96, 0.3);
      }
    }
  `;

  return (
    <>
      {/* Inject keyframes into the document */}
      <style>{keyframesStyle}</style>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Animated dots container */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: config.gap,
          }}
        >
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              style={{
                width: config.dot,
                height: config.dot,
                borderRadius: '50%',
                background: ctaGreen,
                animation: 'b4aDotGlow 1.4s ease-in-out infinite',
                animationDelay: `${index * 0.16}s`,
                boxShadow: `
                  0 0 12px rgba(39, 174, 96, 0.6),
                  0 0 24px rgba(39, 174, 96, 0.4),
                  0 0 36px rgba(39, 174, 96, 0.2)
                `,
              }}
            />
          ))}
        </div>

        {/* Optional loading text */}
        {text !== null && (
          <div
            style={{
              fontSize: config.fontSize,
              marginTop: 16,
              color: 'rgba(249, 249, 249, 0.7)',
              fontWeight: 500,
              letterSpacing: '0.5px',
            }}
          >
            {text}
          </div>
        )}
      </div>
    </>
  );
};

export default B4aLoader;
